import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import Chip, { type ChipProps } from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { PAGES_PER_SPREAD, formatMinutes, localizedFlightTypeLabel, type FlightLog, type FlightSyncStatus } from '@jupi/shared'
import { getClubTimezone } from '@jupi/ui'
import { useTimeBase } from './timeBase'

// One flight, as mik-ng's logbook draws it: the aircraft and flight type
// on a banner, the date as a calendar leaf, and a timeline running
// off-block -> takeoff -> landing -> on-block with the flight time over it
// and the block time under it. Ported from mik-ng's
// packages/ui/src/components/FlightListEntry.tsx, onto mdi icons.
//
// The entry form renders the same timeline as a live preview, so a pilot
// sees the line exactly as the log will show it before saving.

export function FlightLogBanner({ registration, flightType }: { registration: string; flightType: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      <Typography variant="subtitle2" component="p" sx={{ fontWeight: 700 }}>
        {registration}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'right' }}>
        {flightType}
      </Typography>
    </Box>
  )
}

const datePart = (iso: string, options: Intl.DateTimeFormatOptions, locale = 'en-GB', timeZone = getClubTimezone()) =>
  new Intl.DateTimeFormat(locale, { timeZone, ...options }).format(new Date(iso))

// "07:46" in the given zone (the club's by default). Aviation's colon, not
// fi-FI's 07.46: these are the times the pilot copies into the paper
// journey log.
export const clockOf = (iso: string, timeZone = getClubTimezone()) =>
  datePart(iso, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }, 'en-GB', timeZone)

// The calendar date in the given zone, YYYY-MM-DD.
export const dateOf = (iso: string, timeZone = getClubTimezone()) =>
  new Intl.DateTimeFormat('en-CA', { timeZone }).format(new Date(iso))

// Month above, year below the day number; as narrow as the digits allow on
// a phone, so the timeline beside it keeps its room.
export function FlightLogDate({ date, timeZone }: { date: string; timeZone: string }) {
  const { i18n } = useTranslation()
  return (
    <Box sx={{ textAlign: 'center', flexShrink: 0, minWidth: { xs: 36, sm: 56 } }}>
      <Typography
        sx={{ fontSize: { xs: 10, sm: 14 }, textTransform: 'uppercase', color: 'text.secondary', lineHeight: 1.2 }}
      >
        {datePart(date, { month: 'short' }, i18n.language, timeZone)}
      </Typography>
      <Typography sx={{ fontSize: { xs: 28, sm: 40 }, fontWeight: 700, lineHeight: 1 }}>
        {datePart(date, { day: 'numeric' }, 'en-GB', timeZone)}
      </Typography>
      <Typography sx={{ fontSize: { xs: 10, sm: 14 }, color: 'text.secondary', lineHeight: 1.2 }}>
        {datePart(date, { year: 'numeric' }, 'en-GB', timeZone)}
      </Typography>
    </Box>
  )
}

// Markers sit in a fixed-height box so differently sized icons still line
// up with the segments between them.
const ROW_HEIGHT = 24

function TimelineMarker({
  children,
  time,
  bold,
  nextDay,
}: {
  children: ReactNode
  time: string
  bold?: boolean
  nextDay?: boolean
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
      <Box sx={{ height: ROW_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{children}</Box>
      <Typography
        sx={{
          fontSize: bold ? '0.85em' : '0.7em',
          fontWeight: bold ? 700 : 400,
          color: bold ? 'text.primary' : 'text.secondary',
          whiteSpace: 'nowrap',
          mt: 0.25,
        }}
      >
        {time}
        {/* Past midnight: on the day after the flight's date. */}
        {nextDay ? (
          <Box component="sup" sx={{ fontSize: '0.75em', ml: 0.25 }}>
            +1
          </Box>
        ) : null}
      </Typography>
    </Box>
  )
}

function TimelineSegment({ bold }: { bold?: boolean }) {
  return (
    <Box sx={{ height: ROW_HEIGHT, flex: 1, minWidth: 12, display: 'flex', alignItems: 'center', px: 0.5 }}>
      <Box sx={{ width: '100%', height: bold ? 3 : 2, borderRadius: 2, bgcolor: bold ? 'primary.main' : 'divider' }} />
    </Box>
  )
}

export interface TimelineProps {
  departure: string
  arrival: string
  // Clock readings as displayed; empty for a time not yet entered.
  offBlock: string
  takeoff: string
  landing: string
  onBlock: string
  // Which of [offBlock, takeoff, landing, onBlock] fall on the day after
  // the flight's date, marked +1.
  nextDay?: readonly boolean[]
  flightMinutes: number | null
  blockMinutes: number | null
  // "UTC" or the local zone's label, after the block time.
  zoneLabel?: string
}

export function FlightLogTimeline(props: TimelineProps) {
  const { t } = useTranslation()
  const placeholder = '--:--'
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography sx={{ fontWeight: 700 }}>{props.departure || '····'}</Typography>
        <Typography sx={{ fontWeight: 700, fontSize: '1.1em' }}>
          {props.flightMinutes != null ? formatMinutes(props.flightMinutes) : placeholder}
        </Typography>
        <Typography sx={{ fontWeight: 700 }}>{props.arrival || '····'}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', mt: 0.5 }}>
        <TimelineMarker time={props.offBlock || placeholder} nextDay={props.nextDay?.[0]}>
          <Box component={Icon} icon="mdi:account-tie-hat" width={22} sx={{ color: 'text.secondary' }} />
        </TimelineMarker>
        <TimelineSegment />
        <TimelineMarker time={props.takeoff || placeholder} bold nextDay={props.nextDay?.[1]}>
          <Box component={Icon} icon="mdi:airplane-takeoff" width={22} sx={{ color: 'primary.main' }} />
        </TimelineMarker>
        <TimelineSegment bold />
        <TimelineMarker time={props.landing || placeholder} bold nextDay={props.nextDay?.[2]}>
          <Box component={Icon} icon="mdi:airplane-landing" width={22} sx={{ color: 'primary.main' }} />
        </TimelineMarker>
        <TimelineSegment />
        <TimelineMarker time={props.onBlock || placeholder} nextDay={props.nextDay?.[3]}>
          <Box component={Icon} icon="mdi:car-brake-parking" width={22} sx={{ color: 'text.secondary' }} />
        </TimelineMarker>
      </Box>
      <Typography sx={{ textAlign: 'center', fontSize: '0.75em', color: 'text.secondary', mt: 0.25 }}>
        {t('flightLog.blockTime')} {props.blockMinutes != null ? formatMinutes(props.blockMinutes) : placeholder}
        {props.zoneLabel ? ` · ${props.zoneLabel}` : ''}
      </Typography>
    </Box>
  )
}

const SYNC_CHIP: Record<FlightSyncStatus, { color: ChipProps['color']; icon: string }> = {
  queued: { color: 'default', icon: 'mdi:cloud-upload-outline' },
  synced: { color: 'success', icon: 'mdi:cloud-check-outline' },
  failed: { color: 'error', icon: 'mdi:cloud-alert-outline' },
  skipped: { color: 'default', icon: 'mdi:cloud-off-outline' },
}

// Where the flight stands in laskukierros: queued, synced or failed.
function SyncChip({ status }: { status: FlightSyncStatus }) {
  const { t } = useTranslation()
  const { color, icon } = SYNC_CHIP[status]
  return (
    <Tooltip title={t(`flightLog.sync.tooltip.${status}`)}>
      <Chip
        size="small"
        color={color}
        variant="outlined"
        icon={<Icon icon={icon} width={16} />}
        label={t(`flightLog.sync.${status}`)}
      />
    </Tooltip>
  )
}

const surname = (lastName: string | null, name: string | null) => lastName || name || ''

// A row of the flight log list. Opens the flight when `to` is given.
export function FlightLogCard({ flight, to }: { flight: FlightLog; to?: string }) {
  const { t, i18n } = useTranslation()
  const { timeBase, timeZone } = useTimeBase()
  const zoneLabel = timeBase === 'utc' ? 'UTC' : t('flightLog.localShort')
  const crew = flight.crewUserId ? surname(flight.crewLastName, flight.crewName) : null

  const body = (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      <FlightLogBanner registration={flight.registration} flightType={localizedFlightTypeLabel(
          { label: flight.flightTypeLabel, labelSv: flight.flightTypeLabelSv, labelEn: flight.flightTypeLabelEn },
          i18n.language,
        )}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mt: 1 }}>
        <FlightLogDate date={flight.offBlockAt} timeZone={timeZone} />
        <Divider orientation="vertical" flexItem />
        <FlightLogTimeline
          departure={flight.departure}
          arrival={flight.arrival}
          offBlock={clockOf(flight.offBlockAt, timeZone)}
          takeoff={clockOf(flight.takeoffAt, timeZone)}
          landing={clockOf(flight.landingAt, timeZone)}
          onBlock={clockOf(flight.onBlockAt, timeZone)}
          nextDay={[flight.offBlockAt, flight.takeoffAt, flight.landingAt, flight.onBlockAt].map(
            (at) => dateOf(at, timeZone) !== dateOf(flight.offBlockAt, timeZone),
          )}
          flightMinutes={flight.flightMinutes}
          blockMinutes={flight.blockMinutes}
          zoneLabel={zoneLabel}
        />
      </Box>
      <Divider sx={{ mt: 1 }} />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          mt: 0.75,
          color: 'text.secondary',
          fontSize: '0.85em',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {surname(flight.pilotLastName, flight.pilotName)}
            {crew ? ` / ${crew}` : ''} ({flight.personsOnBoard}
            <Icon icon="mdi:account" width={16} />)
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {flight.landings} <Icon icon="mdi:airplane-landing" width={16} />
          </Box>
          {flight.nightMinutes > 0 || flight.nightLandings > 0 ? (
            <Tooltip title={t('flightLog.night')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Icon icon="mdi:weather-night" width={16} /> {formatMinutes(flight.nightMinutes)}
                {flight.nightLandings > 0 ? ` / ${flight.nightLandings}` : ''}
              </Box>
            </Tooltip>
          ) : null}
          {flight.ifrMinutes > 0 ? (
            <Tooltip title="IFR">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Icon icon="mdi:weather-fog" width={16} /> {formatMinutes(flight.ifrMinutes)}
              </Box>
            </Tooltip>
          ) : null}
          {flight.fuelAddedLitres != null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Icon icon="mdi:gas-station-outline" width={16} /> {flight.fuelAddedLitres} l
            </Box>
          ) : null}
          {flight.fuelMeasuredLitres != null ? (
            <Tooltip title={t('flightLog.fuelMeasured')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Icon icon="mdi:gas-station" width={16} /> {flight.fuelMeasuredLitres} l
              </Box>
            </Tooltip>
          ) : null}
          {flight.fuelRemainingLitres != null || flight.fuelState?.remainingLitres != null ? (
            <Tooltip
              title={
                flight.fuelRemainingLitres == null
                  ? `${t('flightLog.fuelRemaining')} (${t(flight.fuelState?.basis === 'measured' ? 'flightLog.fuelMeasuredBasis' : 'flightLog.fuelEstimatedBasis')})`
                  : t('flightLog.fuelRemaining')
              }
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: flight.fuelState?.uncertain && flight.fuelRemainingLitres == null ? 'warning.main' : undefined }}>
                <Icon icon="mdi:gauge" width={16} />{' '}
                {flight.fuelRemainingLitres == null && flight.fuelState?.basis === 'estimated' ? '~' : ''}
                {flight.fuelRemainingLitres ?? flight.fuelState?.remainingLitres} l
              </Box>
            </Tooltip>
          ) : null}
          {flight.oilAddedLitres != null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Icon icon="mdi:oil" width={16} /> {flight.oilAddedLitres} l
            </Box>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {flight.syncStatus ? <SyncChip status={flight.syncStatus} /> : null}
          {flight.overrideDifferenceMinutes != null ? (
            <Tooltip title={flight.totalOverrideReason ?? ''}>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                icon={<Icon icon="mdi:book-edit-outline" width={16} />}
                label={t('flightLog.overridden')}
              />
            </Tooltip>
          ) : null}
          {flight.page != null && flight.row != null ? (
            <Tooltip title={flight.pastLastPage ? t('flightLog.pastLastPage') : flight.verified ? t('flightLog.verifiedFlight') : ''}>
              <Chip
                size="small"
                variant="outlined"
                color={flight.pastLastPage ? 'warning' : 'default'}
                icon={flight.verified ? <Icon icon="mdi:check-decagram" width={16} /> : undefined}
                label={t('flightLog.pageRow', {
                  book: flight.bookNumber,
                  from: flight.page,
                  to: flight.page + PAGES_PER_SPREAD - 1,
                  row: flight.row,
                })}
              />
            </Tooltip>
          ) : null}
          {flight.totalMinutesAfter != null ? (
            <Tooltip title={t('flightLog.totalAfter')}>
              <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                Σ {formatMinutes(flight.totalMinutesAfter)}
              </Box>
            </Tooltip>
          ) : null}
        </Box>
      </Box>
    </Box>
  )

  return (
    <Card variant="outlined">
      {to ? (
        <CardActionArea component={Link} to={to}>
          {body}
        </CardActionArea>
      ) : (
        body
      )}
    </Card>
  )
}
